import { handleResponse } from './response-handler';
import {
  register as registerSDK,
  authenticate as authenticateSDK,
  forgotPassword as forgotPasswordSDK,
  resetPassword as resetPasswordSDK,
  changePassword as changePasswordSDK,
  reenroll as reenrollSDK,
  exchange as exchangeSDK,
} from '../../api/generated/sdk.gen';
import type {
  RegisterRequest,
  AuthenticationRequest,
  AuthenticationResponse,
  ResetPasswordRequest,
  ResetPasswordWithTokenDto,
  ChangePasswordRequest,
  ReenrollRequest,
  ExchangeRequest,
} from '../../api/generated/types.gen';

/**
 * Auth API client for authentication and password management
 *
 * Architecture:
 * - All endpoints now use OpenAPI SDK for consistency and type safety
 * - Errors are transformed through handleResponse() for uniform error handling
 * - All form hooks use the modern React Query onError pattern
 *
 * This ensures:
 * - 100% consistency with domain APIs (schools, users, waste-collection, etc.)
 * - Type-safe requests and responses using auto-generated types
 * - Field-level validation errors displayed below form inputs
 * - Consistent error handling pattern across entire codebase
 *
 * @see response-handler.ts - ApiValidationError preserves field-level errors
 * @see Context7 research: /react-hook-form/react-hook-form - Server-side validation best practices
 */
class AuthAPI {
  /**
   * Change password for authenticated user
   * Returns new token and refresh token
   *
   * @param data - Current password and new password
   * @returns New authentication tokens
   */
  async changePassword(
    data: ChangePasswordRequest
  ): Promise<AuthenticationResponse> {
    const response = await changePasswordSDK({ body: data });
    return handleResponse(response);
  }

  /**
   * Request password reset for forgotten password
   * Public endpoint - sends reset email to user
   *
   * @param data - Email address for password reset
   * @returns Success message confirming email sent
   */
  async requestPasswordReset(data: ResetPasswordRequest): Promise<string> {
    const response = await forgotPasswordSDK({ body: data });
    return handleResponse(response);
  }

  /**
   * Reset password after receiving reset token
   * Public endpoint - validates token and sets new password
   *
   * @param data - Reset token and new password
   * @returns Authentication tokens (auto-login after reset)
   */
  async resetPassword(
    data: ResetPasswordWithTokenDto
  ): Promise<AuthenticationResponse> {
    const response = await resetPasswordSDK({ body: data });
    return handleResponse(response);
  }

  /**
   * Register new user
   * Public endpoint - creates user account
   *
   * @param data - Registration form data (email, password, role, etc.)
   * @returns Authentication tokens (auto-login after registration)
   */
  async register(data: RegisterRequest): Promise<AuthenticationResponse> {
    const response = await registerSDK({ body: data });
    return handleResponse(response);
  }

  /**
   * Authenticate user (login)
   * Public endpoint - validates credentials
   *
   * @param data - Email and password
   * @returns Authentication tokens on successful login
   */
  async login(data: AuthenticationRequest): Promise<AuthenticationResponse> {
    const response = await authenticateSDK({ body: data });
    return handleResponse(response);
  }

  /**
   * Reinscrierea unui elev care avea cont in editia anterioara.
   * Endpoint public - elevul nu are inca token pe editia curenta.
   * Creeaza un cont nou pe editia curenta si intoarce token-urile, ca la login.
   *
   * @param data - Emailul si parola din editia trecuta, plus clasa noua
   * @returns Token-urile contului nou (elevul ramane inactiv pana la acordul parental)
   */
  async reenroll(data: ReenrollRequest): Promise<AuthenticationResponse> {
    const response = await reenrollSDK({ body: data });
    return handleResponse(response);
  }

  /**
   * Schimba un token emis de un provider extern (ex. Google) pe token-urile aplicatiei.
   * Endpoint public - contul trebuie sa existe deja, legat de acel provider.
   *
   * @param data - Numele providerului si token-ul primit de la el
   * @returns Token-urile aplicatiei, ca la login
   */
  async exchange(data: ExchangeRequest): Promise<AuthenticationResponse> {
    const response = await exchangeSDK({ body: data });
    return handleResponse(response);
  }
}

export const authApi = new AuthAPI();
